import React, { useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";

export default function ResetPassword() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password updated. Please sign in.");
      setTimeout(() => nav("/login"), 1400);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not reset password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="reset-password-page"
      className="max-w-[520px] mx-auto px-6 md:px-12 pt-24 md:pt-32 pb-24 fade-up"
    >
      <p className="overline">Account</p>
      <h1 className="font-serif font-light text-3xl md:text-4xl text-brand-dark mt-6 leading-tight">
        Choose a new <span className="italic">password</span>.
      </h1>

      {!token ? (
        <p data-testid="reset-missing-token" className="mt-8 text-brand-neutral">
          This link is missing or invalid.{" "}
          <Link to="/forgot-password" className="text-brand-accent underline underline-offset-4">
            Request a new one
          </Link>
          .
        </p>
      ) : done ? (
        <p data-testid="reset-done" className="mt-8 text-brand-neutral">
          Your password has been updated. Redirecting you to sign in…
        </p>
      ) : (
        <>
          <p className="mt-4 text-brand-neutral">
            For security, choose a password at least 6 characters long.
          </p>
          <form onSubmit={onSubmit} className="mt-10 space-y-6">
            <label className="block">
              <span className="overline block mb-1">New password</span>
              <input
                data-testid="reset-password-new"
                required
                type="password"
                minLength={6}
                className="flushed-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </label>
            <label className="block">
              <span className="overline block mb-1">Confirm password</span>
              <input
                data-testid="reset-password-confirm"
                required
                type="password"
                minLength={6}
                className="flushed-input"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
            </label>
            <button
              data-testid="reset-password-submit"
              disabled={busy}
              className="btn-primary mt-6"
            >
              {busy ? "Updating…" : "Update password"}
            </button>
          </form>
        </>
      )}
    </div>
  );
}
